import React, { useState, useEffect } from 'react';
import { Card, Table, Button, message, Typography, Space, Tag, Alert, Tooltip, Select } from 'antd';
import { DownloadOutlined, FileTextOutlined, FilePdfOutlined, LockOutlined } from '@ant-design/icons';
import apiClient from '../../../api/apiClient';
import { saveAs } from 'file-saver';
import * as XLSX from 'xlsx';
import useHijosPorCiclo from './useHijosPorCiclo';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';

const { Title, Text } = Typography;

const BoletaCalificacionesFamilia = () => {
  const { idFamilia, ciclos, ciclo, setCiclo, hijos, loading } = useHijosPorCiclo();
  const [hijoSeleccionado, setHijoSeleccionado] = useState(null);
  const [calificaciones, setCalificaciones] = useState(null);
  const [loadingSolvencia, setLoadingSolvencia] = useState(false);
  const [loadingCalificaciones, setLoadingCalificaciones] = useState(false);
  const [solvencia, setSolvencia] = useState({});

  // Al cambiar de ciclo: cerrar la boleta abierta y revisar la solvencia de ese ciclo
  useEffect(() => {
    if (!ciclo || !idFamilia) return;
    setHijoSeleccionado(null);
    setCalificaciones(null);
    cargarSolvencia();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ciclo, idFamilia]);

  const cargarSolvencia = async () => {
    setLoadingSolvencia(true);
    setSolvencia({});
    try {
      const solvenciaRes = await apiClient.get(`/pagos/solvencia-familia/${idFamilia}`, { params: { cicloEscolar: ciclo } });

      // Solvencia: solo se evalúan hijos con inscripción activa en el ciclo elegido.
      // Hijos sin inscripción (egresados, retirados) se ignoran para evitar falsos bloqueos.
      if (solvenciaRes.data.success) {
        const raw = solvenciaRes.data.data;
        const idsInscritos = new Set(hijos.map(h => h.IdAlumno));
        const mapa = {};
        const lista = Array.isArray(raw) ? raw : (raw?.hijos || []);
        lista.forEach(h => {
          if (h.IdAlumno !== undefined && idsInscritos.has(h.IdAlumno)) {
            mapa[h.IdAlumno] = h.PuedeVerNotas !== false;
          }
        });
        if (lista.length === 0 && raw?.PuedeVerNotas !== undefined) {
          setSolvencia({ __global: raw.PuedeVerNotas });
        } else {
          setSolvencia(mapa);
        }
      }
    } catch (error) {
      // Sin dato de solvencia no se bloquea (ver puedeVerNotas)
    } finally {
      setLoadingSolvencia(false);
    }
  };

  const puedeVerNotas = (IdAlumno) => {
    if (solvencia.__global !== undefined) return solvencia.__global;
    if (solvencia[IdAlumno] !== undefined) return solvencia[IdAlumno];
    return true; // si no hay dato de solvencia, no bloquear
  };

  const cargarCalificaciones = async (hijo) => {
    setLoadingCalificaciones(true);
    setHijoSeleccionado(hijo);
    try {
      const { IdAlumno, CicloEscolar, IdGrado, IdSeccion, IdJornada } = hijo;

      const response = await apiClient.get(`/boleta-calificaciones/calificaciones/${IdAlumno}`, {
        params: {
          cicloEscolar: CicloEscolar,
          idGrado: IdGrado,
          idSeccion: IdSeccion,
          idJornada: IdJornada
        }
      });

      if (response.data.success) {
        const calificacionesData = response.data.data;

        // Validar que tenga la estructura esperada (backend devuelve 'estudiante', no 'alumno')
        if (!calificacionesData.estudiante) {
          message.warning('Las calificaciones se cargaron pero falta información del estudiante');
        }

        setCalificaciones(calificacionesData);
        message.success('Calificaciones cargadas correctamente');
      } else {
        message.error('Error al cargar las calificaciones');
      }
    } catch (error) {
      message.error('Error al cargar las calificaciones del estudiante');
    } finally {
      setLoadingCalificaciones(false);
    }
  };

  const NOMBRE_COLEGIO = 'Colegio Privado Urbano Mixto Candelaria';

  const cargarLogoBase64 = () =>
    new Promise((resolve) => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement('canvas');
        canvas.width = img.width;
        canvas.height = img.height;
        canvas.getContext('2d').drawImage(img, 0, 0);
        resolve(canvas.toDataURL('image/png'));
      };
      img.onerror = () => resolve(null);
      img.src = '/LogoSinFondo.png';
    });

  const generarPDFBoleta = async (dataBoleta) => {
    try {
      const logoBase64 = await cargarLogoBase64();
      const doc = new jsPDF();
      const pageWidth = doc.internal.pageSize.width;
      const pageHeight = doc.internal.pageSize.height;
      const margin = 12;
      const logoSize = 36;
      const centerX = pageWidth / 2;
      let yPosition = margin;

      // Logo arriba a la derecha
      if (logoBase64) {
        doc.addImage(logoBase64, 'PNG', pageWidth - margin - logoSize, yPosition - 6, logoSize, logoSize);
      }

      // Nombre del colegio y encabezado
      doc.setFontSize(9);
      doc.setFont('helvetica', 'bold');
      doc.text(NOMBRE_COLEGIO, centerX, yPosition + 5, { align: 'center' });

      doc.setFontSize(11);
      doc.text('BOLETA DE CALIFICACIONES', centerX, yPosition + 11, { align: 'center' });

      doc.setFontSize(8);
      doc.setFont('helvetica', 'normal');
      doc.text(`Ciclo Escolar ${dataBoleta.estudiante.CicloEscolar}`, centerX, yPosition + 17, { align: 'center' });

      yPosition += 21;

      // Nombre: Apellidos primero, luego Nombres
      doc.setFontSize(9.5);
      doc.setFont('helvetica', 'bold');
      doc.text(`${dataBoleta.estudiante.Apellidos} ${dataBoleta.estudiante.Nombres}`, margin, yPosition);

      yPosition += 5;
      doc.setFontSize(8);
      doc.setFont('helvetica', 'normal');
      doc.text(`Código: ${dataBoleta.estudiante.Codigo}`, margin, yPosition);
      doc.text(
        `Grado: ${dataBoleta.estudiante.NombreGrado} ${dataBoleta.estudiante.NombreSeccion}  |  Jornada: ${dataBoleta.estudiante.NombreJornada}`,
        pageWidth - margin,
        yPosition,
        { align: 'right' }
      );

      yPosition += 4;
      // Línea separadora azul
      doc.setDrawColor(41, 128, 185);
      doc.setLineWidth(0.3);
      doc.line(margin, yPosition, pageWidth - margin, yPosition);
      yPosition += 2;

      // La tabla empieza después del texto O después del logo, lo que sea más bajo
      yPosition = Math.max(yPosition, margin + logoSize + 2);

      const tableData = dataBoleta.cursos.map((curso) => {
        const unidades = [1, 2, 3, 4].map(numUnidad => {
          const unidad = curso.unidades.find(u => u.NumeroUnidad === numUnidad);

          // Si no existe la unidad o la nota es null/undefined, mostrar guión
          if (!unidad || unidad.NotaFinal === null || unidad.NotaFinal === undefined) {
            return '-';
          }

          // Si la nota es 0 o mayor, mostrarla (0 es válido)
          return unidad.NotaFinal.toString();
        });

        // Promedio: mostrar guión si es null, 0, o vacío
        const promedioMostrar = (curso.promedio === null || curso.promedio === undefined || curso.promedio === '')
          ? '-'
          : curso.promedio.toString();

        return [
          curso.NombreCurso,
          ...unidades,
          promedioMostrar
        ];
      });

      // Fila de promedio por unidad
      const promsUnidad = [1, 2, 3, 4].map((numUnidad) => {
        const notas = dataBoleta.cursos
          .map(c => c.unidades.find(u => u.NumeroUnidad === numUnidad)?.NotaFinal)
          .filter(n => n !== null && n !== undefined);
        if (notas.length === 0) return null;
        return Math.round((notas.reduce((a, b) => a + b, 0) / notas.length) * 100) / 100;
      });
      const promsCursos = dataBoleta.cursos
        .map(c => c.promedio)
        .filter(p => p !== null && p !== undefined && p !== '');
      const promFilaGeneral = promsCursos.length > 0
        ? Math.round((promsCursos.reduce((a, b) => a + Number(b), 0) / promsCursos.length) * 100) / 100
        : null;

      const filaBg = [214, 228, 255];
      const celda = (valor) => ({
        content: valor !== null ? String(valor) : '-',
        styles: {
          fontStyle: 'bold',
          halign: 'center',
          fillColor: filaBg,
          textColor: valor === null ? [150, 150, 150] : valor >= 60 ? [22, 119, 255] : [255, 77, 79],
        },
      });

      tableData.push([
        { content: 'Promedio por Unidad', styles: { fontStyle: 'bold', fillColor: filaBg, textColor: [30, 30, 30] } },
        ...promsUnidad.map(celda),
        celda(promFilaGeneral),
      ]);

      autoTable(doc, {
        startY: yPosition,
        head: [['Curso', 'U1', 'U2', 'U3', 'U4', 'Prom']],
        body: tableData,
        theme: 'grid',
        headStyles: { fillColor: [41, 128, 185], textColor: 255, fontStyle: 'bold', halign: 'center', fontSize: 9 },
        columnStyles: {
          0: { halign: 'left', cellWidth: 100 },
          1: { halign: 'center', cellWidth: 17 },
          2: { halign: 'center', cellWidth: 17 },
          3: { halign: 'center', cellWidth: 17 },
          4: { halign: 'center', cellWidth: 17 },
          5: { halign: 'center', cellWidth: 20, fontStyle: 'bold', fillColor: [240, 240, 240] }
        },
        styles: { fontSize: 9, cellPadding: 2.5, lineColor: [200, 200, 200], lineWidth: 0.1 },
        margin: { left: margin, right: margin }
      });

      // Promedio General
      const finalY = doc.lastAutoTable.finalY + 5;
      doc.setFontSize(9);
      doc.setFont('helvetica', 'bold');
      const promedioGeneralTexto = (dataBoleta.promedioGeneral === null || dataBoleta.promedioGeneral === undefined || dataBoleta.promedioGeneral === '')
        ? 'PROMEDIO GENERAL: - puntos'
        : `PROMEDIO GENERAL: ${dataBoleta.promedioGeneral} puntos`;
      doc.text(promedioGeneralTexto, centerX, finalY, { align: 'center' });

      // Footer
      doc.setFontSize(7);
      doc.setFont('helvetica', 'italic');
      doc.text(`Generado el ${new Date().toLocaleDateString('es-GT')}`, centerX, pageHeight - 5, { align: 'center' });

      return doc;

    } catch (error) {
      throw error;
    }
  };

  const imprimirBoleta = async (hijo) => {
    try {
      message.loading({ content: 'Generando PDF...', key: 'pdf' });

      const { IdAlumno, CicloEscolar, IdGrado, IdSeccion, IdJornada } = hijo;

      const response = await apiClient.get(
        `/boleta-calificaciones/calificaciones/${IdAlumno}`,
        {
          params: {
            cicloEscolar: CicloEscolar,
            idGrado: IdGrado,
            idSeccion: IdSeccion,
            idJornada: IdJornada
          }
        }
      );

      if (response.data.success) {
        const dataBoleta = response.data.data;
        const doc = await generarPDFBoleta(dataBoleta);

        const fileName = `Boleta_${dataBoleta.estudiante.Codigo}_${dataBoleta.estudiante.Nombres}_${dataBoleta.estudiante.Apellidos}.pdf`;
        doc.save(fileName);

        message.success({ content: 'PDF generado exitosamente', key: 'pdf' });
      }
    } catch (error) {
      message.error({ content: 'Error al generar el PDF', key: 'pdf' });
    }
  };

  const exportarExcel = () => {
    if (!calificaciones) {
      message.warning('No hay calificaciones para exportar');
      return;
    }

    const { estudiante, cursos, promedioGeneral } = calificaciones;

    // Validar que estudiante exista
    if (!estudiante) {
      message.error('No se encontró información del estudiante');
      return;
    }

    // Crear datos para el Excel
    const datosExcel = [];

    // Encabezado
    datosExcel.push(['BOLETA DE CALIFICACIONES']);
    datosExcel.push([]);
    datosExcel.push(['Estudiante:', `${estudiante.Nombres} ${estudiante.Apellidos}`]);
    datosExcel.push(['Código:', estudiante.Codigo]);
    datosExcel.push(['Grado:', estudiante.NombreGrado]);
    datosExcel.push(['Sección:', estudiante.NombreSeccion]);
    datosExcel.push(['Jornada:', estudiante.NombreJornada]);
    datosExcel.push(['Ciclo Escolar:', estudiante.CicloEscolar]);
    datosExcel.push([]);

    // Tabla de calificaciones
    datosExcel.push(['Curso', 'Unidad 1', 'Unidad 2', 'Unidad 3', 'Unidad 4', 'Promedio']);

    cursos.forEach(curso => {
      const fila = [curso.NombreCurso];
      // Validar las 4 unidades
      [1, 2, 3, 4].forEach(numUnidad => {
        const unidad = curso.unidades.find(u => u.NumeroUnidad === numUnidad);
        fila.push(unidad && unidad.NotaFinal !== null && unidad.NotaFinal !== undefined ? unidad.NotaFinal : '-');
      });
      fila.push(curso.promedio !== null && curso.promedio !== undefined && curso.promedio !== '' ? curso.promedio : '-');
      datosExcel.push(fila);
    });

    datosExcel.push([]);
    const promedioFinal = promedioGeneral !== null && promedioGeneral !== undefined && promedioGeneral !== '' ? promedioGeneral : '-';
    datosExcel.push(['PROMEDIO GENERAL:', '', '', '', '', promedioFinal]);

    // Crear libro y hoja
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.aoa_to_sheet(datosExcel);

    // Ajustar anchos de columna
    ws['!cols'] = [
      { wch: 30 }, // Curso
      { wch: 12 }, // Unidad 1
      { wch: 12 }, // Unidad 2
      { wch: 12 }, // Unidad 3
      { wch: 12 }, // Unidad 4
      { wch: 12 }  // Promedio
    ];

    XLSX.utils.book_append_sheet(wb, ws, 'Boleta');

    // Descargar archivo
    const filename = `Boleta_${estudiante.Nombres}_${estudiante.Apellidos}_${estudiante.CicloEscolar}.xlsx`;
    const excelBuffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
    saveAs(new Blob([excelBuffer]), filename);

    message.success('Excel descargado correctamente');
  };

  // Columnas para la tabla de hijos
  const columnasHijos = [
    {
      title: 'Carnet',
      dataIndex: 'IdAlumno',
      key: 'IdAlumno',
      width: 100,
    },
    {
      title: 'Nombre Completo',
      key: 'nombreCompleto',
      render: (_, record) => `${record.Nombres} ${record.Apellidos}`,
    },
    {
      title: 'Grado',
      dataIndex: 'NombreGrado',
      key: 'NombreGrado',
    },
    {
      title: 'Sección',
      dataIndex: 'NombreSeccion',
      key: 'NombreSeccion',
      width: 100,
      align: 'center',
    },
    {
      title: 'Jornada',
      dataIndex: 'NombreJornada',
      key: 'NombreJornada',
      width: 120,
    },
    {
      title: 'Solvencia',
      key: 'solvencia',
      width: 110,
      align: 'center',
      render: (_, record) =>
        puedeVerNotas(record.IdAlumno)
          ? <Tag color="green">Solvente</Tag>
          : <Tag color="red" icon={<LockOutlined />}>Insolvente</Tag>,
    },
    {
      title: 'Acciones',
      key: 'acciones',
      width: 250,
      align: 'center',
      render: (_, record) => {
        const puede = puedeVerNotas(record.IdAlumno);
        return (
          <Space>
            <Tooltip title={!puede ? 'No está al día en los pagos' : ''}>
              <Button
                type="primary"
                icon={puede ? <FileTextOutlined /> : <LockOutlined />}
                onClick={() => puede && cargarCalificaciones(record)}
                loading={loadingCalificaciones && hijoSeleccionado?.IdAlumno === record.IdAlumno}
                disabled={!puede}
              >
                Ver Boleta
              </Button>
            </Tooltip>
            <Tooltip title={!puede ? 'No está al día en los pagos' : ''}>
              <Button
                icon={puede ? <FilePdfOutlined /> : <LockOutlined />}
                onClick={() => puede && imprimirBoleta(record)}
                disabled={!puede}
              >
                PDF
              </Button>
            </Tooltip>
          </Space>
        );
      },
    },
  ];

  // Columnas para la tabla de calificaciones
  const columnasCalificaciones = [1, 2, 3, 4].map((numUnidad) => ({
    title: `Unidad ${numUnidad}`,
    key: `unidad${numUnidad}`,
    width: 100,
    align: 'center',
    render: (_, record) => {
      const unidad = record.unidades.find(u => u.NumeroUnidad === numUnidad);

      if (unidad?.Bloqueada) {
        return <Tag color="orange">🔒 {unidad.FechaPublicacion.split('-').reverse().join('/')}</Tag>;
      }

      // Si no existe la unidad o la nota es null/undefined, mostrar guión
      if (!unidad || unidad.NotaFinal === null || unidad.NotaFinal === undefined) {
        return <Tag color="default">-</Tag>;
      }

      const nota = unidad.NotaFinal;
      const color = nota >= 60 ? 'blue' : nota > 0 ? 'red' : 'default';
      return <Tag color={color}>{nota}</Tag>;
    },
  }));

  const todasColumnasCalificaciones = [
    {
      title: 'Curso',
      dataIndex: 'NombreCurso',
      key: 'NombreCurso',
      fixed: 'left',
      width: 250,
    },
    ...columnasCalificaciones,
    {
      title: 'Promedio',
      dataIndex: 'promedio',
      key: 'promedio',
      width: 100,
      align: 'center',
      fixed: 'right',
      render: (promedio) => {
        // Mostrar guión si el promedio es null, undefined o vacío
        if (promedio === null || promedio === undefined || promedio === '') {
          return <Tag color="default" style={{ fontSize: 14, fontWeight: 'bold' }}>-</Tag>;
        }
        const color = promedio >= 60 ? 'blue' : promedio > 0 ? 'red' : 'default';
        return <Tag color={color} style={{ fontSize: 14, fontWeight: 'bold' }}>{promedio}</Tag>;
      },
    },
  ];

  const hijosInsolventes = hijos.filter(h => !puedeVerNotas(h.IdAlumno));

  return (
    <div style={{ padding: 24 }}>
      <Title level={2}>Boleta de Calificaciones</Title>
      <Text type="secondary">Consulta las calificaciones de tus hijos</Text>

      {hijosInsolventes.length > 0 && (
        <Alert
          type="warning"
          showIcon
          style={{ marginTop: 16 }}
          message="Acceso restringido por pagos pendientes"
          description={
            hijosInsolventes.length === hijos.length
              ? 'Ningún estudiante está al día en los pagos. No puedes consultar boletas de calificaciones hasta regularizar el estado de cuenta.'
              : `Los siguientes estudiantes no están al día en los pagos y no pueden consultar sus boletas: ${hijosInsolventes.map(h => `${h.Nombres} ${h.Apellidos}`).join(', ')}.`
          }
        />
      )}

      {/* Tabla de Hijos */}
      <Card
        title="Mis Hijos"
        style={{ marginTop: 24 }}
        loading={loading || loadingSolvencia}
        extra={ciclos.length > 0 && (
          <Space>
            <Text type="secondary">Ciclo escolar:</Text>
            <Select
              value={ciclo}
              onChange={setCiclo}
              options={ciclos.map(c => ({ value: c, label: c }))}
              style={{ width: 100 }}
            />
          </Space>
        )}
      >
        <Table
          columns={columnasHijos}
          dataSource={hijos}
          rowKey="IdAlumno"
          pagination={false}
          bordered
          locale={{ emptyText: 'No hay hijos inscritos en este ciclo' }}
        />
      </Card>

      {/* Tabla de Calificaciones */}
      {calificaciones && calificaciones.estudiante && (
        <Card
          title={
            <Space direction="vertical" size={0}>
              <Text strong style={{ fontSize: 18 }}>
                Boleta de Calificaciones - {calificaciones.estudiante.Nombres} {calificaciones.estudiante.Apellidos}
              </Text>
              <Space size="large" style={{ marginTop: 8 }}>
                <Text type="secondary">Grado: {calificaciones.estudiante.NombreGrado}</Text>
                <Text type="secondary">Sección: {calificaciones.estudiante.NombreSeccion}</Text>
                <Text type="secondary">Ciclo: {calificaciones.estudiante.CicloEscolar}</Text>
              </Space>
            </Space>
          }
          extra={
            <Space>
              <Button
                type="primary"
                icon={<FilePdfOutlined />}
                onClick={() => imprimirBoleta(hijoSeleccionado)}
                size="large"
              >
                Generar PDF
              </Button>
              <Button
                icon={<DownloadOutlined />}
                onClick={exportarExcel}
                size="large"
              >
                Descargar Excel
              </Button>
            </Space>
          }
          style={{ marginTop: 24 }}
          loading={loadingCalificaciones}
        >
          <Table
            columns={todasColumnasCalificaciones}
            dataSource={calificaciones.cursos}
            rowKey="idCurso"
            pagination={false}
            bordered
            scroll={{ x: 1000 }}
            summary={(cursos) => {
              // Calcular promedio por unidad
              const promediosUnidad = [1, 2, 3, 4].map((numUnidad) => {
                const notas = cursos
                  .map(c => c.unidades.find(u => u.NumeroUnidad === numUnidad)?.NotaFinal)
                  .filter(n => n !== null && n !== undefined);
                if (notas.length === 0) return null;
                const prom = Math.round((notas.reduce((a, b) => a + b, 0) / notas.length) * 100) / 100;
                return prom;
              });

              const promedioCursos = cursos
                .map(c => c.promedio)
                .filter(p => p !== null && p !== undefined && p !== '');
              const promedioFila = promedioCursos.length > 0
                ? Math.round((promedioCursos.reduce((a, b) => a + Number(b), 0) / promedioCursos.length) * 100) / 100
                : null;

              const renderCelda = (valor) => {
                if (valor === null) return <Tag color="default" style={{ fontWeight: 'bold' }}>-</Tag>;
                const color = valor >= 60 ? 'blue' : 'red';
                return <Tag color={color} style={{ fontWeight: 'bold' }}>{valor}</Tag>;
              };

              return (
                <Table.Summary.Row style={{ background: '#fafafa' }}>
                  <Table.Summary.Cell index={0}>
                    <Text strong>Promedio por Unidad</Text>
                  </Table.Summary.Cell>
                  {promediosUnidad.map((prom, i) => (
                    <Table.Summary.Cell key={i} index={i + 1} align="center">
                      {renderCelda(prom)}
                    </Table.Summary.Cell>
                  ))}
                  <Table.Summary.Cell index={5} align="center">
                    {renderCelda(promedioFila)}
                  </Table.Summary.Cell>
                </Table.Summary.Row>
              );
            }}
            footer={() => {
              const promedioGeneral = calificaciones.promedioGeneral;
              const promedioTexto = promedioGeneral === null || promedioGeneral === undefined || promedioGeneral === '' ? '-' : promedioGeneral;
              const color = promedioGeneral >= 60 ? 'blue' : promedioGeneral > 0 ? 'red' : 'default';

              return (
                <div style={{ textAlign: 'right', fontWeight: 'bold', fontSize: 16 }}>
                  <Space>
                    <Text>PROMEDIO GENERAL:</Text>
                    <Tag
                      color={color}
                      style={{ fontSize: 16, padding: '4px 16px' }}
                    >
                      {promedioTexto}
                    </Tag>
                  </Space>
                </div>
              );
            }}
          />
        </Card>
      )}
    </div>
  );
};

export default BoletaCalificacionesFamilia;
